import { http } from '@/api/http'
import type { ListQuery } from '@/api/query'
import type { RequestOptions } from '../crud'

export type BalanceGroupKind = 'importer' | 'distributor' | 'trader' | 'logist'

/** A balance sheet of the company («Балансы»: AFF, IML, CAMA, UZB LOGISTICS): a set of firms with its own books. */
export type BalanceGroup = {
  id: string
  code: string
  name: string
  kind: BalanceGroupKind
  legal_entity_id: string
  legal_entity_name: string
  /** A trader's or logist's procurement partner: a proforma whose «Продавец» it is goes through this trader. */
  partner_id: string
  partner_name: string
  is_default: boolean
  is_active: boolean
  sort_order: number
  firms: { id: string; name: string; party_id: string }[]
  /** The balance's products (AFF's «A» products); a product of none is the default balance's. */
  products: { id: string; name: string }[]
  /** Users fixed to this balance (trader / logist accounts). */
  users: { profile_id: string; name: string }[]
  /** The «AFF | ChF» parent companies it sits under: the «Баланс» report lists it under those only. */
  parent_group_ids?: string[]
  created_at: string
  updated_at: string
}

export type BalanceGroupInput = {
  code: string
  name: string
  kind: BalanceGroupKind
  legal_entity_id: string
  partner_id: string
  legal_entity_ids: string[]
  product_ids: string[]
  is_default: boolean
  is_active: boolean
  sort_order: number
}

const base = '/finance/balance-groups'

/** The endpoint returns every balance group at once; the list is searched and paged here. */
export const balanceGroupsService = {
  async all(options?: RequestOptions) {
    const { data } = await http.get<BalanceGroup[]>(base, options)
    return data ?? []
  },
  async list(query: ListQuery = {}, options?: RequestOptions) {
    const search = (query.search ?? '').trim().toLowerCase()
    const items = (await balanceGroupsService.all(options)).filter(
      (item) => !search || item.name.toLowerCase().includes(search) || item.code.toLowerCase().includes(search),
    )
    const limit = query.limit ?? items.length
    const start = ((query.page ?? 1) - 1) * limit
    return { count: items.length, items: items.slice(start, start + limit) }
  },
  async get(id: string, options?: RequestOptions) {
    const found = (await balanceGroupsService.all(options)).find((item) => item.id === id)
    if (!found) throw new Error('balance group not found')
    return found
  },
  async create(body: BalanceGroupInput) {
    const { data } = await http.post<BalanceGroup>(base, body)
    return data
  },
  async update(id: string, body: BalanceGroupInput) {
    const { data } = await http.put<BalanceGroup>(`${base}/${encodeURIComponent(id)}`, body)
    return data
  },
  async remove(id: string) {
    await http.delete(`${base}/${encodeURIComponent(id)}`)
  },
}
